import { Event } from '@bugsnag/js';
import memoize from 'fast-memoize';

import { getInstance as getConfigServiceInstance } from 'Lib/ConfigService';
import { LEVER_BUGSNAG_SCOPE_FILTERS } from 'Lib/ConfigService/levers';
import type { ExtractLeverValueType, LeverKey } from 'Lib/ConfigService/types';
import { I13N_CATEGORY_APP } from 'Lib/constants/i13nSpec';
import { ErrorTypes } from 'Lib/errors';
import { getLeverValue } from 'Lib/utils/getLeverValue';

const RELEASE_STAGING_HOSTNAME = 'staging.tinder.com';

const FALLBACK_SAMPLE_RATE = 0.01;
const FALLBACK_LOG_SAMPLE_RATE = 0.001;

/**
 * Get formatted levers in alphabetical order
 * @returns { Object } experiments string[] and featureFlags { name: string, variant: string }[]
 */
const getFormattedLevers = memoize(
  (levers: Partial<{ [TLeverKey in LeverKey]: ExtractLeverValueType<TLeverKey> }>) => {
    const experiments: string[] = [];
    const featureFlags: { name: string; variant: string }[] = [];

    Object.keys(levers)
      .sort()
      .forEach((key) => {
        const value = levers[key];
        experiments.push(`${key}:${value}`);
        featureFlags.push({ name: key, variant: value.toString() });
      });
    return { experiments, featureFlags };
  }
);

export default class BugsnagEventBuilder {
  constructor(private event: Event) {
    // Initialize the event misc metadata
    this.setMisc(this.event.originalError.misc);
    this.setBreadcrumbMessages();
    this.setSampleRate();
    this.setType();
    // TODO: consider to set below values using react-i13n I13N_TYPE_SET_VALUE instead as these are not event specific
    this.setFeatureFlags();
    this.setIsLite();
  }

  /**
   * Get the sample rate imposed on the event
   * @returns { number } sample rate
   */
  getSampleRate(): number {
    return this.event.getMetadata('misc').totalFilterPercentage;
  }

  /**
   * Check whether the event is an instance of any of the error classes
   * @param errors
   * @returns { boolean } result
   */
  isInstanceOf(errors: ErrorTypes[]) {
    return errors.some((error) => this.event.originalError instanceof error);
  }

  /**
   * Check whether the event is not an instance of every error classes
   * @param errors
   * @returns { boolean } result
   */
  isNotInstanceOf(errors: ErrorTypes[]) {
    return errors.every((error) => !(this.event.originalError instanceof error));
  }

  /**
   * Set EXPERIMENTS and Features according to the levers
   */
  private setFeatureFlags() {
    const configService = getConfigServiceInstance();
    const levers = configService.getAllLeverValues();
    const { experiments, featureFlags } = getFormattedLevers(levers);

    this.event.addMetadata('experiments', { experiments });
    this.event.addFeatureFlags(featureFlags);
  }

  /**
   * Set MISC data
   */
  private setMisc(data: Record<string, string | string[] | number> = {}) {
    this.event.addMetadata('misc', {
      ...data,
      ...(this.event.getMetadata('misc') ?? {}),
    });
  }

  /**
   * Set totalFilterPercentage that is the sample rate imposed on the event
   */
  private setTotalFilterPercentage(totalFilterPercentage: number) {
    this.setMisc({ totalFilterPercentage });
  }

  /**
   * Set the sample rate to be imposed on the event
   * Lever ref: https://tools.gotinder.com/#/phoenix/levers/bugsnag.scope_filters
   */
  private setSampleRate() {
    const {
      errors,
      originalError: {
        customSampleRate = -1,
        misc: { tags = [], type = undefined } = {},
        severity,
        shouldSample = true,
      },
      unhandled = false,
    } = this.event;
    const { errorClass = '' } = errors[0];
    const isReleaseStaging = window.location.hostname === RELEASE_STAGING_HOSTNAME;

    // Always report errors on staging and development env
    // NOTE: BugSnag will not report errors in development env unless localhost is added to the allowed domains in the Project settings
    if (__DEVELOPMENT__ || __PRODTEST__ || isReleaseStaging || !shouldSample) {
      this.setTotalFilterPercentage(1);
      return;
    }

    // Set a custom sample rate for an individual event is discouraged and should be used as a last resort
    if (customSampleRate !== -1) {
      return customSampleRate;
    }

    try {
      const sampleRate = FALLBACK_SAMPLE_RATE;
      const scopeFilters = getLeverValue(LEVER_BUGSNAG_SCOPE_FILTERS);

      // If the event is for logging purpose, impose the log sample rate
      if (severity === 'info') {
        this.setTotalFilterPercentage(JSON.parse(scopeFilters).log ?? FALLBACK_LOG_SAMPLE_RATE);
        return;
      }
      if (unhandled) {
        // Precedence rate limit rule for unhandled errors: error type -> unhandled -> default -> 0.01
        this.setTotalFilterPercentage(
          JSON.parse(scopeFilters).errorType?.[errorClass] ??
            JSON.parse(scopeFilters).unhandled ??
            JSON.parse(scopeFilters).default ??
            sampleRate
        );
        return;
      }
      /**
       * Precedence rate limit rule for handled errors: fatal errors // saga name -> error type -> default -> 0.01
       * 1. Always report the fatal errors (app rendering errors)
       * 2. Impose the rate limit by Saga name
       * 3. If there is no rate limit for Saga name, using the error type
       * 4. If there is no rate limit for error type, using the default sample rate
       * 5. Otherwise impose 0.01 when failed to retrieve the default
       */
      this.setTotalFilterPercentage(
        type === I13N_CATEGORY_APP
          ? 1
          : JSON.parse(scopeFilters).sagaName?.[tags[0]] ??
              JSON.parse(scopeFilters).errorType?.[errorClass] ??
              JSON.parse(scopeFilters).default ??
              sampleRate
      );
    } catch {
      // continue regardless of error
    }
  }

  /**
   * Set breadcrumbMessages that collects all the console messages under BREADCRUMBS for further filtering
   */
  private setBreadcrumbMessages() {
    const breadcrumbMessages: string[] = [];
    const { breadcrumbs } = this.event;

    breadcrumbs.forEach((breadcrumb) => {
      // Only add log type for now
      if (breadcrumb.type === 'log') {
        breadcrumbMessages.push(breadcrumb.metadata['[0]']);
      }
    });

    this.setMisc({ breadcrumbMessages });
  }

  /**
   * Set isLite that justifies whether the event is for TinderLite app
   */
  private setIsLite() {
    this.setMisc({ isLite: process.env.IS_LITE });
  }

  /**
   * Set type that is the i13n category of the event
   */
  private setType() {
    const { type } = this.event.originalError.misc ?? {};
    this.setMisc({ type });
  }
}
